import {
  CONTENT_VERSION,
  PROTOCOL_VERSION,
  type AbilityEntityWireState,
  type ClientMessage,
  type CityAllyUnitState,
  type CityBuildingState,
  type DefenseSegmentState,
  type HandshakeMessage,
  type PlayerHostRelayClientMessage,
  type PlayerHostRelayServerMessage,
  type PlayerPublicState,
  type PublicRoomState,
  type ServerMessage,
} from "../../../../packages/game-core/src";
import { PlayerHostedRoom } from "./PlayerHostedRoom";
import {
  createEndpointStorageKey,
  resolveRuntimeEndpoints,
  type RuntimeEndpoints,
} from "./RuntimeEndpoints";

export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

export interface StoredSession {
  roomCode: string;
  playerId: string;
  token: string;
  role?: "host" | "guest" | "dedicated";
  playerHostReceipt?: string;
  playerHostReceiptExpiresAt?: number;
}

type MessageListener = (message: ServerMessage) => void;
type StatusListener = (status: ConnectionStatus) => void;

const SESSION_KEY = "bastion.phase1.session";

export class GameNetworkClient {
  private readonly endpoints: RuntimeEndpoints;
  private readonly sessionKey: string;
  private socket?: WebSocket;
  private hostedRoom?: PlayerHostedRoom;
  private activeRole?: "host" | "guest" | "dedicated";
  private pendingHandshake?: HandshakeMessage;
  private pendingHost?: { name: string; seed?: string };
  private readonly messageListeners = new Set<MessageListener>();
  private readonly statusListeners = new Set<StatusListener>();
  private reconnectTimer?: number;
  private reconnectAttempt = 0;
  private intentionalClose = false;
  private status: ConnectionStatus = "disconnected";
  private publishedHostedResultRoomCode?: string;
  private playerHostReceipt?: {
    roomCode: string;
    receipt: string;
    expiresAt: number;
  };
  private cachedPlayers: PlayerPublicState[] = [];
  private cachedAbilityEntities: AbilityEntityWireState[] = [];
  private cachedDefenses: DefenseSegmentState[] = [];
  private cachedCityBuildings: CityBuildingState[] = [];
  private cachedCityAllies: CityAllyUnitState[] = [];

  playerId?: string;
  roomCode?: string;

  constructor() {
    this.endpoints = resolveRuntimeEndpoints();
    const sessionEndpoint =
      this.endpoints.networkMode === "player-host"
        ? this.endpoints.playerHostRelayUrl
        : this.endpoints.webSocketUrl;
    this.sessionKey = `${SESSION_KEY}:${createEndpointStorageKey(
      sessionEndpoint,
    )}`;
  }

  onMessage(listener: MessageListener): () => void {
    this.messageListeners.add(listener);
    return () => this.messageListeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  createRoom(name: string, seed?: string): void {
    if (this.endpoints.networkMode === "dedicated") {
      this.activeRole = "dedicated";
      this.openDedicated({
        type: "create",
        name,
        seed: seed?.trim() || undefined,
        protocolVersion: PROTOCOL_VERSION,
        contentVersion: CONTENT_VERSION,
      });
      return;
    }
    this.openHostedRoom(name, seed?.trim() || undefined);
  }

  joinRoom(roomCode: string, name: string): void {
    const handshake: HandshakeMessage = {
      type: "join",
      roomCode,
      name,
      protocolVersion: PROTOCOL_VERSION,
      contentVersion: CONTENT_VERSION,
    };
    if (this.endpoints.networkMode === "dedicated") {
      this.activeRole = "dedicated";
      this.openDedicated(handshake);
      return;
    }
    this.openHostedGuest(handshake);
  }

  resumeSession(): boolean {
    const session = this.getStoredSession();
    if (!session) {
      return false;
    }
    if (
      this.endpoints.networkMode === "player-host" &&
      session.role === "host"
    ) {
      this.clearSession();
      return false;
    }
    const handshake: HandshakeMessage = {
      type: "reconnect",
      roomCode: session.roomCode,
      playerId: session.playerId,
      token: session.token,
      protocolVersion: PROTOCOL_VERSION,
      contentVersion: CONTENT_VERSION,
    };
    if (this.endpoints.networkMode === "dedicated") {
      this.activeRole = "dedicated";
      this.openDedicated(handshake);
    } else {
      this.openHostedGuest(handshake);
    }
    return true;
  }

  send(message: ClientMessage): boolean {
    if (this.activeRole === "host" && this.hostedRoom) {
      this.hostedRoom.sendLocal(message);
      return true;
    }
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    if (this.activeRole === "guest") {
      this.sendRelay({
        type: "sendToHost",
        payload: JSON.stringify(message),
      });
      return true;
    }
    this.socket.send(JSON.stringify(message));
    return true;
  }

  getStoredSession(): StoredSession | undefined {
    const parsed = this.readStoredSession();
    if (
      parsed &&
      this.endpoints.networkMode === "player-host" &&
      parsed.role === "host"
    ) {
      return undefined;
    }
    return parsed;
  }

  getReportSession(): StoredSession | undefined {
    return this.readStoredSession();
  }

  private readStoredSession(): StoredSession | undefined {
    const raw = localStorage.getItem(this.sessionKey);
    if (!raw) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(raw) as StoredSession;
      if (parsed.roomCode && parsed.playerId && parsed.token) {
        return parsed;
      }
    } catch {
      localStorage.removeItem(this.sessionKey);
    }
    return undefined;
  }

  clearSession(): void {
    localStorage.removeItem(this.sessionKey);
    this.playerId = undefined;
    this.roomCode = undefined;
    this.playerHostReceipt = undefined;
  }

  close(): void {
    this.intentionalClose = true;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
    }
    this.hostedRoom?.close();
    this.hostedRoom = undefined;
    this.socket?.close(1000, "Client closed");
    this.socket = undefined;
    this.activeRole = undefined;
    this.publishedHostedResultRoomCode = undefined;
    this.playerHostReceipt = undefined;
    this.cachedPlayers = [];
    this.cachedAbilityEntities = [];
    this.cachedDefenses = [];
    this.setStatus("disconnected");
  }

  getApiUrl(pathname: string): string {
    return new URL(pathname, this.endpoints.apiBaseUrl).toString();
  }

  getServerUrl(): string {
    return this.endpoints.networkMode === "player-host"
      ? this.endpoints.playerHostRelayUrl
      : this.endpoints.webSocketUrl;
  }

  isPlayerHosted(): boolean {
    return this.endpoints.networkMode === "player-host";
  }

  isLocalHost(): boolean {
    return this.activeRole === "host";
  }

  getPlaytestReceipt(): string | undefined {
    if (!this.isPlayerHosted()) {
      return undefined;
    }
    if (
      this.playerHostReceipt &&
      this.playerHostReceipt.roomCode === this.roomCode &&
      this.playerHostReceipt.expiresAt > Date.now()
    ) {
      return this.playerHostReceipt.receipt;
    }
    const session = this.getReportSession();
    return session?.playerHostReceiptExpiresAt &&
      session.playerHostReceiptExpiresAt > Date.now()
      ? session.playerHostReceipt
      : undefined;
  }

  private openDedicated(handshake: HandshakeMessage): void {
    this.pendingHandshake = handshake;
    this.openSocket(
      this.endpoints.webSocketUrl,
      handshake.type === "reconnect",
      (socket) => socket.send(JSON.stringify(handshake)),
      (raw) => this.handleDedicatedMessage(raw),
    );
  }

  private openHostedRoom(name: string, seed?: string): void {
    this.pendingHost = { name, seed };
    this.pendingHandshake = undefined;
    this.activeRole = "host";
    this.openSocket(
      this.endpoints.playerHostRelayUrl,
      false,
      () => this.sendRelay({ type: "createHost" }),
      (raw) => this.handleRelayMessage(raw),
    );
  }

  private openHostedGuest(handshake: HandshakeMessage): void {
    if (handshake.type === "create") {
      return;
    }
    this.pendingHost = undefined;
    this.pendingHandshake = handshake;
    this.activeRole = "guest";
    this.openSocket(
      this.endpoints.playerHostRelayUrl,
      handshake.type === "reconnect",
      () =>
        this.sendRelay({
          type: "joinGuest",
          roomCode: handshake.roomCode,
        }),
      (raw) => this.handleRelayMessage(raw),
    );
  }

  private openSocket(
    url: string,
    reconnecting: boolean,
    onOpen: (socket: WebSocket) => void,
    onMessage: (raw: string) => void,
  ): void {
    this.intentionalClose = false;
    this.hostedRoom?.close();
    this.hostedRoom = undefined;
    this.socket?.close(4001, "Replacing connection");
    this.setStatus(reconnecting ? "reconnecting" : "connecting");

    const socket = new WebSocket(url);
    this.socket = socket;
    socket.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      onOpen(socket);
    });
    socket.addEventListener("message", (event) => onMessage(String(event.data)));
    socket.addEventListener("close", () => {
      if (this.socket !== socket) {
        return;
      }
      this.socket = undefined;
      this.hostedRoom?.close();
      this.hostedRoom = undefined;
      if (
        !this.intentionalClose &&
        this.activeRole !== "host" &&
        this.getStoredSession()
      ) {
        this.scheduleReconnect();
      } else {
        this.setStatus("disconnected");
      }
    });
    socket.addEventListener("error", () => {
      if (this.status === "connecting") {
        this.setStatus("disconnected");
      }
    });
  }

  private handleDedicatedMessage(raw: string): void {
    const message = parseJson<ServerMessage>(raw);
    if (message) {
      this.handleServerMessage(message);
    }
  }

  private handleRelayMessage(raw: string): void {
    const message = parseJson<PlayerHostRelayServerMessage>(raw);
    if (!message) {
      return;
    }
    switch (message.type) {
      case "roomCreated": {
        const pending = this.pendingHost;
        if (!pending) {
          return;
        }
        this.hostedRoom = new PlayerHostedRoom({
          roomCode: message.roomCode,
          hostName: pending.name,
          seed: pending.seed,
          onLocalMessage: (serverMessage) =>
            this.handleServerMessage(serverMessage),
          onPeerMessage: (peerId, payload) =>
            this.sendRelay({ type: "sendToPeer", peerId, payload }),
        });
        this.publishedHostedResultRoomCode = undefined;
        this.playerHostReceipt = undefined;
        break;
      }
      case "roomJoined":
        if (this.pendingHandshake) {
          this.sendRelay({
            type: "sendToHost",
            payload: JSON.stringify(this.pendingHandshake),
          });
        }
        break;
      case "peerJoined":
        this.hostedRoom?.addPeer(message.peerId);
        break;
      case "peerLeft":
        this.hostedRoom?.removePeer(message.peerId);
        break;
      case "peerMessage":
        this.hostedRoom?.receivePeerMessage(message.peerId, message.payload);
        break;
      case "hostMessage": {
        const serverMessage = parseJson<ServerMessage>(message.payload);
        if (serverMessage) {
          this.handleServerMessage(serverMessage);
        }
        break;
      }
      case "roomClosed":
        this.emitMessage({
          type: "error",
          code: "HOST_LEFT",
          message: message.reason,
        });
        this.intentionalClose = true;
        if (!this.getPlaytestReceipt()) {
          this.clearSession();
        }
        this.socket?.close(4004, "Host left");
        this.setStatus("disconnected");
        break;
      case "playtestReceipt":
        this.playerHostReceipt = {
          roomCode: message.roomCode,
          receipt: message.receipt,
          expiresAt: message.expiresAt,
        };
        this.persistPlayerHostReceipt();
        break;
      case "relayError":
        this.emitMessage({
          type: "error",
          code: message.code,
          message: message.message,
        });
        if (this.status === "connecting" || this.status === "reconnecting") {
          this.setStatus("disconnected");
        }
        break;
      case "relayPong":
        break;
    }
  }

  private handleServerMessage(message: ServerMessage): void {
    if (message.type === "welcome" || message.type === "snapshot") {
      const compatibilityError = getRoomStateCompatibilityError(message.state);
      if (compatibilityError) {
        this.intentionalClose = true;
        this.clearSession();
        this.cachedPlayers = [];
        this.cachedAbilityEntities = [];
        this.cachedDefenses = [];
        this.cachedCityBuildings = [];
        this.cachedCityAllies = [];
        this.emitMessage({
          type: "error",
          code: "SNAPSHOT_REJECTED",
          message: compatibilityError,
        });
        this.socket?.close(4008, "Snapshot version mismatch");
        this.setStatus("disconnected");
        return;
      }
      if (message.state.players.length > 0) {
        this.cachedPlayers = message.state.players;
      }
      if (message.state.playerMotions) {
        const motions = new Map(
          message.state.playerMotions.map((motion) => [motion[0], motion]),
        );
        this.cachedPlayers = this.cachedPlayers.map((player) => {
          const motion = motions.get(player.id);
          if (!motion) {
            return player;
          }
          return {
            ...player,
            x: motion[1],
            y: motion[2],
            health: motion[3],
            mana: motion[4],
            status: motion[5],
            inputSequence: motion[6],
            connected: motion[7],
            aiControlled: motion[8],
            reviveProgressMs: motion[9],
            respawnAtMs: motion[10],
          };
        });
      }
      if (this.cachedPlayers.length > 0) {
        message.state.players = this.cachedPlayers;
      }
      if (message.state.abilityEntities !== undefined) {
        this.cachedAbilityEntities = message.state.abilityEntities;
      }
      if (message.state.abilityEntityAnchors) {
        const anchors = new Map(
          message.state.abilityEntityAnchors.map((anchor) => [anchor[0], anchor]),
        );
        this.cachedAbilityEntities = this.cachedAbilityEntities
          .filter((entity) => anchors.has(entity.id))
          .map((entity) => {
            const anchor = anchors.get(entity.id)!;
            return {
              ...entity,
              x: anchor[1],
              y: anchor[2],
              health: anchor[3],
            };
          });
      }
      message.state.abilityEntities = this.cachedAbilityEntities;
      if (message.state.defenses.length > 0) {
        this.cachedDefenses = message.state.defenses;
      }
      if (message.state.defenseAnchors) {
        const anchors = new Map(
          message.state.defenseAnchors.map((anchor) => [anchor[0], anchor]),
        );
        this.cachedDefenses = this.cachedDefenses.map((defense) => {
          const anchor = anchors.get(defense.id);
          return anchor
            ? {
                ...defense,
                health: anchor[1],
                maxHealth: anchor[2],
                armor: anchor[3],
                destroyed: anchor[4],
              }
            : defense;
        });
      }
      if (this.cachedDefenses.length > 0) {
        message.state.defenses = this.cachedDefenses;
      }
      if (message.state.cityBuildings.length > 0) {
        this.cachedCityBuildings = message.state.cityBuildings;
      }
      if (message.state.cityBuildingAnchors) {
        const anchors = new Map(
          message.state.cityBuildingAnchors.map((anchor) => [anchor[0], anchor]),
        );
        this.cachedCityBuildings = this.cachedCityBuildings.map((building) => {
          const anchor = anchors.get(building.id);
          return anchor
            ? {
                ...building,
                health: anchor[1],
                destroyed: anchor[2],
                activeUnitCount: anchor[3],
              }
            : building;
        });
      }
      if (this.cachedCityBuildings.length > 0) {
        message.state.cityBuildings = this.cachedCityBuildings;
      }
      if (message.state.cityAllies !== undefined) {
        this.cachedCityAllies = message.state.cityAllies;
      }
      if (message.state.cityAllyAnchors) {
        const anchors = new Map(
          message.state.cityAllyAnchors.map((anchor) => [anchor[0], anchor]),
        );
        this.cachedCityAllies = this.cachedCityAllies
          .filter((ally) => anchors.has(ally.id))
          .map((ally) => {
            const anchor = anchors.get(ally.id)!;
            return {
              ...ally,
              x: anchor[1],
              y: anchor[2],
              health: anchor[3],
            };
          });
      }
      message.state.cityAllies = this.cachedCityAllies;
    } else if (message.type === "abilityEntities") {
      const byId = new Map(
        this.cachedAbilityEntities.map((entity) => [entity.id, entity]),
      );
      for (const entity of message.entities) {
        byId.set(entity.id, entity);
      }
      this.cachedAbilityEntities = [...byId.values()];
    }
    if (message.type === "welcome") {
      this.playerId = message.playerId;
      this.roomCode = message.roomCode;
      localStorage.setItem(
        this.sessionKey,
        JSON.stringify({
          roomCode: message.roomCode,
          playerId: message.playerId,
          token: message.token,
          role: this.activeRole,
          playerHostReceipt:
            this.playerHostReceipt?.roomCode === message.roomCode
              ? this.playerHostReceipt.receipt
              : undefined,
          playerHostReceiptExpiresAt:
            this.playerHostReceipt?.roomCode === message.roomCode
              ? this.playerHostReceipt.expiresAt
              : undefined,
        } satisfies StoredSession),
      );
      this.setStatus("connected");
    }
    this.emitMessage(message);
    if (
      message.type === "result" ||
      (message.type === "welcome" &&
        ["victory", "draw", "defeat"].includes(message.state.phase)) ||
      (message.type === "snapshot" &&
        ["victory", "draw", "defeat"].includes(message.state.phase))
    ) {
      this.publishHostedResultIfReady();
    }
  }

  private emitMessage(message: ServerMessage): void {
    for (const listener of this.messageListeners) {
      listener(message);
    }
  }

  private sendRelay(message: PlayerHostRelayClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private publishHostedResultIfReady(): void {
    if (
      this.activeRole !== "host" ||
      !this.hostedRoom ||
      this.publishedHostedResultRoomCode === this.roomCode
    ) {
      return;
    }
    const publication = this.hostedRoom.getPlaytestPublication();
    if (!publication) {
      return;
    }
    this.sendRelay({
      type: "publishPlaytestResult",
      ...publication,
    });
    this.publishedHostedResultRoomCode = this.roomCode;
  }

  private persistPlayerHostReceipt(): void {
    const receipt = this.playerHostReceipt;
    if (!receipt) {
      return;
    }
    const session = this.getReportSession();
    if (!session || session.roomCode !== receipt.roomCode) {
      return;
    }
    localStorage.setItem(
      this.sessionKey,
      JSON.stringify({
        ...session,
        playerHostReceipt: receipt.receipt,
        playerHostReceiptExpiresAt: receipt.expiresAt,
      } satisfies StoredSession),
    );
  }

  private scheduleReconnect(): void {
    this.setStatus("reconnecting");
    const session = this.getStoredSession();
    if (!session) {
      this.setStatus("disconnected");
      return;
    }
    const delay = Math.min(5_000, 800 * 1.6 ** this.reconnectAttempt);
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      const handshake: HandshakeMessage = {
        type: "reconnect",
        roomCode: session.roomCode,
        playerId: session.playerId,
        token: session.token,
        protocolVersion: PROTOCOL_VERSION,
        contentVersion: CONTENT_VERSION,
      };
      if (this.endpoints.networkMode === "dedicated") {
        this.activeRole = "dedicated";
        this.openDedicated(handshake);
      } else {
        this.openHostedGuest(handshake);
      }
    }, delay);
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) {
      return;
    }
    this.status = status;
    for (const listener of this.statusListeners) {
      listener(status);
    }
  }
}

export function getRoomStateCompatibilityError(
  state: Pick<PublicRoomState, "protocolVersion" | "contentVersion">,
): string | undefined {
  if (
    state.protocolVersion === PROTOCOL_VERSION &&
    state.contentVersion === CONTENT_VERSION
  ) {
    return undefined;
  }
  return `房间快照版本不兼容：需要协议 ${PROTOCOL_VERSION} / 内容 ${CONTENT_VERSION}；P4.11 采矿快照已封存，未载入。`;
}

function parseJson<T>(raw: string): T | undefined {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}
